<template>
  <div class="team-sprint-column h-100" :class="teamCardClass">
    <!-- Progress card -->
    <div class="stat-card stat-card--progress card mb-0">
      <div class="stat-card-accent" aria-hidden="true"></div>
      <div class="card-body progress-card-body py-5">
        <div class="progress-top d-flex align-items-center">
          <div class="progress-side progress-side--chart">
            <div
              class="donut-chart"
              :style="{ background: donutGradient }"
              role="img"
              :aria-label="`${donePercent}% complete`"
            >
              <div class="donut-chart__hole">
                <span class="donut-chart__value">{{ donePercent }}%</span>
              </div>
            </div>
          </div>

          <div class="progress-side progress-side--stats">
            <div class="progress-stats-inner">
              <h4 class="stat-value mb-1">
                <span class="text-success">{{ doneCount }}</span>
                <span class="text-dark"> / {{ totalCount }}</span>
              </h4>
              <p class="stat-caption mb-0">Tasks</p>
              <div class="stat-divider stat-divider--thick" aria-hidden="true"></div>
              <p class="stat-sub stat-sub--in-progress mb-0">
                <span class="font-weight-bold">{{ inProgressPercent }}%</span>
                <span> In-progress</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team header -->
    <div class="team-header text-center">
      <h5 class="team-title mb-2">{{ teamTitle }}</h5>
      <span class="team-date-pill">{{ dateRangeLabel }}</span>
    </div>

    <!-- Detail card -->
    <div class="stat-card card mb-0">
      <div class="stat-card-accent" aria-hidden="true"></div>
      <div class="card-body px-3 py-3">
        <div class="deploy-block text-center">
          <p class="deploy-label deploy-label--ready mb-2.5">Ready To Deploy</p>
          <p class="deploy-count mb-0" :class="countToneClass(readyToDeployCount)">
            <span class="deploy-count-num">{{ readyToDeployCount }}</span>
            <span class="deploy-count-suffix"> Tasks</span>
          </p>
        </div>

        <div class="stat-divider stat-divider--thin" aria-hidden="true"></div>

        <div class="deploy-block text-center">
          <p class="deploy-label deploy-label--waiting mb-2">Waiting To Confirm</p>
          <p class="deploy-count mb-0" :class="countToneClass(waitingToConfirmCount)">
            <span class="deploy-count-num">{{ waitingToConfirmCount }}</span>
            <span class="deploy-count-suffix"> Tasks</span>
          </p>
        </div>

        <div class="stat-divider stat-divider--thin" aria-hidden="true"></div>

        <div class="status-list">
          <div v-for="row in statusRows" :key="row.key" class="status-row">
            <span class="status-label-wrap">
              <i class="fas status-icon" :class="row.iconClass" aria-hidden="true"></i>
              <span class="status-label">{{ row.label }}</span>
            </span>
            <span class="status-value">
              <span class="font-weight-bold">{{ row.count }}</span>
              <span class="text-secondary"> Tasks</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const TEAM_CARD_CLASS = {
  a: "team-card-a",
  b: "team-card-b",
  c: "team-card-c",
};

const TEAM_TITLE = {
  a: "TEAM - A",
  b: "TEAM - B",
  c: "TEAM - C",
};

const STATUS_ICONS = {
  done: "fa-check-circle text-success",
  to_do: "fa-flag text-primary",
  in_progress: "fa-sync-alt text-info",
  testing: "fa-search text-purple",
  bug: "fa-bug text-danger",
};

export default {
  name: "TeamSprintSummaryCard",
  props: {
    team: { type: String, required: true },
    teamLabel: { type: String, default: "" },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    doneCount: { type: Number, default: 0 },
    totalCount: { type: Number, default: 0 },
    inProgressPercent: { type: Number, default: 0 },
    readyToDeployCount: { type: Number, default: 0 },
    waitingToConfirmCount: { type: Number, default: 0 },
    toDoCount: { type: Number, default: 0 },
    doneStatusCount: { type: Number, default: 0 },
    inProgressStatusCount: { type: Number, default: 0 },
    testingCount: { type: Number, default: 0 },
    bugCount: { type: Number, default: 0 },
  },
  computed: {
    teamCardClass() {
      return TEAM_CARD_CLASS[this.team] || TEAM_CARD_CLASS.a;
    },
    teamTitle() {
      return TEAM_TITLE[this.team] || String(this.teamLabel || "").toUpperCase();
    },
    donePercent() {
      if (!this.totalCount) return 0;
      return Math.round((this.doneCount / this.totalCount) * 100);
    },
    donutGradient() {
      const pct = Math.min(100, Math.max(0, this.donePercent));
      return `conic-gradient(#344767 0% ${pct}%, #e9ecef ${pct}% 100%)`;
    },
    dateRangeLabel() {
      const start = this.formatDate(this.startDate);
      const end = this.formatDate(this.endDate);
      if (start && end) return `${start} - ${end}`;
      return "No active sprint";
    },
    statusRows() {
      return [
        { key: "done", label: "Done", count: this.doneStatusCount, iconClass: STATUS_ICONS.done },
        { key: "to_do", label: "To Do", count: this.toDoCount, iconClass: STATUS_ICONS.to_do },
        {
          key: "in_progress",
          label: "In-progress",
          count: this.inProgressStatusCount,
          iconClass: STATUS_ICONS.in_progress,
        },
        { key: "testing", label: "Testing", count: this.testingCount, iconClass: STATUS_ICONS.testing },
        { key: "bug", label: "Bug", count: this.bugCount, iconClass: STATUS_ICONS.bug },
      ];
    },
  },
  methods: {
    countToneClass(count) {
      return Number(count) > 0 ? "deploy-count--success" : "deploy-count--neutral";
    },
    formatDate(val) {
      if (!val) return "";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.team-sprint-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-card {
  position: relative;
  border-radius: 12px !important;
  border: 1px solid #f0f2f5;
  background-color: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04) !important;
  overflow: hidden;
}

.stat-card-accent {
  display: block;
  width: 35%;
  max-width: 8.5rem;
  height: 6px;
  margin: 0;
  border-radius: 0 999px 999px 0;
  background: #8392ab;
}

.team-sprint-column.team-card-a .stat-card-accent {
  background: #2152ff;
}

.team-sprint-column.team-card-b .stat-card-accent {
  background: #82d616;
}

.team-sprint-column.team-card-c .stat-card-accent {
  background: #fb6340;
}

.team-sprint-column.team-card-a .stat-card {
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.04),
    0 0 28px rgba(33, 82, 255, 0.12) !important;
  border-color: rgba(33, 82, 255, 0.1);
}

.team-sprint-column.team-card-b .stat-card {
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.04),
    0 0 28px rgba(130, 214, 22, 0.14) !important;
  border-color: rgba(130, 214, 22, 0.12);
}

.team-sprint-column.team-card-c .stat-card {
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.04),
    0 0 28px rgba(251, 99, 64, 0.14) !important;
  border-color: rgba(251, 99, 64, 0.12);
}

.progress-card-body {
  padding-left: 1.5rem;
  padding-right: 1rem;
}

.progress-top {
  gap: 1rem;
}

.progress-side {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
}

.progress-side--chart {
  justify-content: flex-start;
  padding-left: 0.75rem;
  padding-right: 0;
}

.progress-side--stats {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 0.25rem;
  padding-right: 0.75rem;
}

.progress-stats-inner {
  width: 100%;
  max-width: 13rem;
  margin-right: 0.75rem;
  text-align: center;
}

.donut-chart {
  width: 10rem;
  height: 10rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.donut-chart__hole {
  width: 8rem;
  height: 8rem;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-chart__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #344767;
  line-height: 1;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #344767;
  line-height: 1.2;
}

.stat-caption {
  font-size: 0.65rem;
  font-weight: 600;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-sub {
  font-size: 0.9rem;
  font-weight: 600;
}

.stat-sub--in-progress {
  color: #3333cc;
}

.stat-divider {
  background: #e9ecef;
  border-radius: 1px;
}

.stat-divider--thick {
  width: 100%;
  height: 3px;
  margin: 0.75rem auto;
}

.team-header {
  padding: 1.5rem 0;
}

.team-title {
  font-size: 1rem;
  font-weight: 700;
  color:rgb(14, 40, 85);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.team-sprint-column.team-card-a .team-date-pill {
  background: rgba(79, 111, 227, 0.4);
}

.team-sprint-column.team-card-b .team-date-pill {
  background: rgba(131, 214, 22, 0.27);
}

.team-sprint-column.team-card-c .team-date-pill {
  background: rgba(251, 98, 64, 0.21);
}

.team-date-pill {
  display: inline-block;
  padding: 0.3rem 0.85rem;
  border-radius: 999px;
  background: #f8f9fa;
  color:rgb(2, 11, 28);
  font-size: 0.75rem;
  font-weight: 600;
}

.stat-divider--thin {
  width: 100%;
  height: 1px;
  background: #e9ecef;
  margin: 0.7rem 0;
}

.deploy-label {
  font-size: 0.9rem;
  font-weight: 700;
}

.deploy-label--ready {
  color: #006633;
}

.deploy-label--waiting {
  color: #990033;
}

.deploy-count {
  font-size: 0.88rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.deploy-icon {
  font-size: 0.95rem;
}

.deploy-count--neutral {
  color: #344767;
}

.deploy-count--success {
  color: #17ad37;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.status-label-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.status-icon {
  width: 1rem;
  text-align: center;
  font-size: 0.82rem;
  flex-shrink: 0;
}

.text-purple {
  color: #7928ca;
}

.status-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #8392ab;
}

.status-value {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  color: #344767;
}
</style>
